import type {
  OffsetPaginationMeta,
  OffsetPaginationQueryParams,
} from "@shared/contracts";

/**
 * Laravel's `pagination_data` block, verbatim.
 *
 * Built by `ApiResponse::formatPaginateData()`. It sits *next to* `data`, not
 * inside it, and is `null` on every non-list response.
 *
 * Numbers are typed loosely because a paginator that has been through a JSON
 * round trip can hand back `per_page` as a string.
 */
export interface LaravelPaginationData {
  current_page: number | string;
  from: number | null;
  to: number | null;
  total: number | string;
  per_page: number | string;
  total_pages: number | string;
  is_first_page: boolean;
  is_last_page: boolean;
}

const isPaginationData = (value: unknown): value is LaravelPaginationData =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  "current_page" in value;

/**
 * `pagination_data` → the `{ page, limit, total, … }` meta the app consumes.
 *
 * When the block is missing, synthesize a single page from what we know rather
 * than throwing: a list route that has been misconfigured server-side then
 * merely looks un-paginated, which beats turning a perfectly readable list into
 * an error screen.
 */
export function toOffsetMeta(
  paginationData: unknown,
  itemCount: number,
  params?: OffsetPaginationQueryParams
): OffsetPaginationMeta {
  if (!isPaginationData(paginationData)) {
    return {
      page: params?.page ?? 1,
      limit: params?.limit ?? itemCount,
      total: itemCount,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
      from: itemCount > 0 ? 1 : null,
      to: itemCount > 0 ? itemCount : null,
    };
  }

  const page = Number(paginationData.current_page);
  const totalPages = Number(paginationData.total_pages);

  return {
    page,
    limit: Number(paginationData.per_page),
    total: Number(paginationData.total),
    totalPages,
    // Prefer the server's own flags; fall back to arithmetic if a future
    // backend drops them.
    hasNext:
      typeof paginationData.is_last_page === "boolean"
        ? !paginationData.is_last_page
        : page < totalPages,
    hasPrev:
      typeof paginationData.is_first_page === "boolean"
        ? !paginationData.is_first_page
        : page > 1,
    from: paginationData.from,
    to: paginationData.to,
  };
}
